// The Internal user administration screen. Self-service account settings are
// `account`; this is the staff view of somebody else's login.
import { AUTH } from './auth'
import { COMMON } from './common'
import { USER_ROLE_LABELS as ROLE } from './enum-labels'
import { FIELD_LABELS } from './fields'
import { TERMS } from './terms'

/** HubSpot has no contact under the account's address. Three surfaces report
 *  it and each adds what follows, so the clause itself is written once. */
const UNKNOWN_TO_HUBSPOT = 'HubSpot kennt diese E-Mail-Adresse nicht'

/** What a sync writes, and therefore what BrickUp refuses to let an advisor
 *  edit. Reads as a plural subject. */
const HUBSPOT_OWNED_FIELDS = 'Name, Anrede und Telefon'

/** Where those fields are kept. Follows a plural subject. */
const OWNED_BY_HUBSPOT = 'kommen aus HubSpot und werden dort geändert'

export const USERS = {
  create: 'Benutzer anlegen',
  search: 'Name oder E-Mail suchen …',
  passwordOptional: 'Passwort (leer = automatisch generiert)',
  customers: 'Kunden',
  others: 'Andere',
  roles: FIELD_LABELS.roles.label,
  /** Under the role picker: which roles combine. */
  rolesHint: `${ROLE.ARCHITECT} und ${ROLE.SALES} lassen sich kombinieren. ${ROLE.CUSTOMER}, ${ROLE.ADMIN} und ${ROLE.ROOT} stehen allein.`,
  /** Fetching a customer's data from HubSpot, and saying yes to it. */
  hubSpot: {
    fetch: 'Aus HubSpot holen',
    apply: 'Aus HubSpot übernehmen',
    title: 'Daten aus HubSpot',
    /** Above the comparison: nothing is written until the button below is
     *  pressed. */
    description:
      'Das hat HubSpot zu dieser Adresse. Übernommen wird erst, wenn du unten bestätigst — Felder, zu denen HubSpot nichts sagt, bleiben wie sie sind.',
    stored: 'Bisher',
    incoming: 'Aus HubSpot',
    nothing: 'HubSpot sagt dazu nichts',
    notFound: `${UNKNOWN_TO_HUBSPOT}.`,
    /** The fields a sync owns, wherever a surface has to name them. */
    ownedFields: HUBSPOT_OWNED_FIELDS,
    ownedBy: OWNED_BY_HUBSPOT,
    unknown: UNKNOWN_TO_HUBSPOT,
    applied: 'Daten aus HubSpot übernommen.',
    confirm: 'Übernehmen',
    /** Next to the e-mail box while creating an account. */
    fetchHint: 'Erst die E-Mail-Adresse eintragen.',
    /** A quiet line where the form filled itself in — so nobody wonders where
     *  the values came from. */
    filled: 'Aus HubSpot vorausgefüllt. Bitte prüfen.',
  },

  /** The pane beside the list. */
  detail: {
    masterData: TERMS.masterData,
    phones: FIELD_LABELS.phone.label,
    addPhone: COMMON.addNamed(FIELD_LABELS.phone.label),
    editPhone: COMMON.editNamed(FIELD_LABELS.phone.label),
    removePhone: COMMON.removeNamed(FIELD_LABELS.phone.label),
    removePhoneConfirm: 'Die Nummer wird von diesem Konto entfernt.',
    noPhones: 'Keine Nummer hinterlegt',
    emails: TERMS.email,
    loginAddress: 'Login',
    /** Under the list: what the extra addresses are and are not for. */
    emailsHint:
      'Die obere Adresse ist der Login. Weitere werden nur festgehalten — es geht keine Post an sie und man meldet sich nicht damit an.',
    addEmail: COMMON.addNamed(TERMS.email),
    editEmail: COMMON.editNamed(TERMS.email),
    removeEmail: COMMON.removeNamed(TERMS.email),
    removeEmailConfirm:
      'Die Adresse wird von diesem Konto entfernt. Der Login bleibt davon unberührt.',
    address: FIELD_LABELS.address.label,
    noAddress: 'Keine Adresse hinterlegt',
    addAddress: COMMON.addNamed(FIELD_LABELS.address.label),
    editAddress: COMMON.editNamed(FIELD_LABELS.address.label),
    removeAddress: COMMON.removeNamed(FIELD_LABELS.address.label),
    removeAddressConfirm: 'Die Adresse wird von diesem Konto entfernt.',
  },
  errors: {
    rolesEmpty: 'Wähle mindestens eine Rolle aus.',
    roleNotCombinable: `${ROLE.CUSTOMER}, ${ROLE.ADMIN} und ${ROLE.ROOT} lassen sich mit keiner anderen Rolle kombinieren.`,
    lastNameRequired: 'Ohne Namen geht es nicht.',
    organisationNotAllowed: `Nur ein ${ROLE.CUSTOMER} kann eine Organisation sein.`,
    organisationHasNoPersonFields:
      'Eine Organisation hat einen Namen und sonst nichts — keine Anrede, keinen Vornamen, keinen Titel.',
    pluralSalutationNotAllowed: `„Familie“ und „Eheleute“ gibt es nur beim ${ROLE.CUSTOMER}.`,
    pluralSalutationHasNoFirstName:
      '„Familie“ und „Eheleute“ sprechen mehrere an — daneben passt kein Vorname und kein Titel.',
    salutationRequiredWithoutFirstName:
      'Ohne Vornamen brauchen wir eine Anrede, sonst hat die App keine Möglichkeit, diesen Kontakt anzusprechen.',
    hubSpotSyncCustomersOnly:
      'Berater werden über ihr eigenes Profil mit HubSpot abgeglichen.',
    emailAlreadyRecorded:
      'Diese Adresse ist bei diesem Konto schon hinterlegt.',
    notKnownToHubSpot: `${UNKNOWN_TO_HUBSPOT}. Ohne Kontakt dort gibt es nichts zu übernehmen.`,
    nameOwnedByHubSpot: `${HUBSPOT_OWNED_FIELDS} dieses Beraters ${OWNED_BY_HUBSPOT}.`,
    customerNumberTaken: (number: string) =>
      `Die Kundennummer ${number} ist schon vergeben.`,
    customerNumberNotAllowed: `Eine Kundennummer hat nur ein ${ROLE.CUSTOMER}.`,
  },
  /** An account that has never been signed in to. Read by every surface that
   *  renders `lastSeenAt`, so the users table and a project's customer row
   *  cannot say it two ways. */
  neverSeen: 'Noch nie angemeldet',
  delete: {
    title: 'Benutzer löschen',
    message:
      'Dieser Benutzer wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
  },
  resetPassword: {
    title: AUTH.forgotPasswordTitle,
    description: (email: string) =>
      `Ein neues Passwort wird gesetzt und an ${email} gesendet. Aktive Sitzungen werden beendet.`,
    placeholder: 'Leer lassen für zufälliges Passwort',
    hint: (min: number) =>
      `Ein selbst gesetztes Passwort braucht mindestens ${min} Zeichen. Das Passwort wird nicht angezeigt — der Benutzer erhält es per E-Mail.`,
    submit: 'Zurücksetzen',
    success: 'Passwort zurückgesetzt und per E-Mail an den Benutzer gesendet.',
    error: 'Passwort konnte nicht zurückgesetzt werden.',
  },
} as const
